import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, inject, ChangeDetectorRef } from '@angular/core';

import { Building3dViewerComponent } from '../building-3d-viewer/building-3d-viewer.component';
import { BuildingModelAssetsService } from '../../../core/services/building-model-assets.service';
import { BuildingModelAssetBundle } from '../../../core/models/building-model-assets.model';
import { ConfigurationManagerService } from '../../../core/services/configuration-manager.service';
import { FLOOR_LEAFLET_TILE_OPACITY } from '../floor-leaflet.constants';
import { firstValueFrom } from 'rxjs';
import L from 'leaflet';

@Component({
  selector: 'app-blender-floor-viewer',
  standalone: true,
  imports: [Building3dViewerComponent],
  templateUrl: './blender-floor-viewer.component.html',
  styleUrl: './blender-floor-viewer.component.css',
})

export class BlenderFloorViewerComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() assetResolutionKey: string | null = null;
  @Input() assetBundle: BuildingModelAssetBundle | null = null;
  @Input() floorMapEntity: any = {};
  @Input() roomStatusColors: any = {};
  @Input() alertRecords: any[] = [];
  @Input() selectedRoomId: string | null = null;
  @Input() showResetCamera: boolean = true;
  @Input() liveUserData: any = null;
  @Input() liveUserAssetConfig: any = null;
  /** Reuse the floor-map Leaflet layer underneath instead of creating a second map. */
  @Input() useSharedLeafletBackground = false;
  /** True for the fullscreen modal instance (uses its own Leaflet map). */
  @Input() isFullscreenInstance = false;
  /** Copy center/zoom from the sidebar floor-map so both views match. */
  @Input() syncLeafletView: { lat: number; lng: number; zoom: number } | null = null;
  /** When false, Leaflet init is deferred until the container becomes visible. */
  @Input() mapVisible = true;
  /** When true, the 3D building view is rendered upside down. */
  @Input() flipVertical = false;
  /** When true, displays beacon markers on the 3D building view. */
  @Input() showBeacons = false;

  @Output() roomClicked = new EventEmitter<{ roomId: string; floorId: string; mesh: any; roomName?: string; roomType?: string; metadata?: Record<string, unknown>; screenshot?: string }>();
  @Output() userClicked = new EventEmitter<{ userId: string; roomId: string; mesh: any }>();
  /** Emitted when fullscreen instance is ready (modal content is outside parent ViewChildren). */
  @Output() viewerReady = new EventEmitter<BlenderFloorViewerComponent>();
  /** Emitted when the 3D building model loading state changes. */
  @Output() loadingChange = new EventEmitter<boolean>();

  @ViewChild(Building3dViewerComponent) building3dViewer!: Building3dViewerComponent;
  @ViewChild('leafletMap') leafletMapElement?: ElementRef<HTMLDivElement>;

  private isProcessingRoomClick = false;
  private lastEmittedRoomId: string | null = null;
  private lastEmitTime: number = 0;
  private parsedMetadata: any = null;
  private sceneReady = false;
  private floorMap: L.Map | undefined;
  private resizeObserver: ResizeObserver | null = null;
  private leafletResizeObserver: ResizeObserver | null = null;
  private leafletInitTimer: ReturnType<typeof setTimeout> | null = null;
  private rawAssetBundle: BuildingModelAssetBundle | null = null;
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private buildingModelAssets: BuildingModelAssetsService
  ) { }

  async ngAfterViewInit(): Promise<void> {
    this.scheduleLeafletInit();
    this.setupSharedLayoutResizeObserver();
    await this.loadBuildingModel();
    if (this.isFullscreenInstance) {
      queueMicrotask(() => this.viewerReady.emit(this));
      queueMicrotask(() => this.refreshLeafletBackground());
    }
  }

  /** Resize Babylon when the sidebar map stack changes size. */
  public refreshViewport(): void {
    this.building3dViewer?.refreshViewport();
  }

  /** Reuse GLB bundle already loaded in the sidebar viewer (fullscreen modal). */
  public applyPreloadedAssetBundle(bundle: BuildingModelAssetBundle): void {
    this.rawAssetBundle = bundle;
    this.updateAssetBundleForFloor();
  }

  private setupSharedLayoutResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    if (this.useSharedLeafletBackground && !this.isFullscreenInstance) {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.building3dViewer?.refreshViewport();
      this.floorMap?.invalidateSize();
    });
    this.resizeObserver.observe(this.hostElement.nativeElement);
  }

  private scheduleLeafletInit(): void {
    if (this.useSharedLeafletBackground) {
      return;
    }

    if (!this.mapVisible) {
      return;
    }

    if (this.leafletInitTimer) {
      clearTimeout(this.leafletInitTimer);
    }

    this.leafletInitTimer = setTimeout(() => {
      this.leafletInitTimer = null;
      if (!this.mapVisible || this.useSharedLeafletBackground) {
        return;
      }
      this.initLeafletMap();
    }, this.isFullscreenInstance ? 0 : 100);
  }

  /** Called after the fullscreen modal finishes opening. */
  public refreshLeafletBackground(): void {
    if (this.useSharedLeafletBackground) {
      return;
    }

    if (!this.floorMap) {
      this.scheduleLeafletInit();
    } else {
      this.applyLeafletView();
      [0, 200, 500].forEach((delay) => {
        setTimeout(() => {
          this.floorMap?.invalidateSize();
          this.applyLeafletView();
        }, delay);
      });
    }

    [0, 150, 400, 800].forEach((delay) => {
      setTimeout(() => this.building3dViewer?.refreshViewport(), delay);
    });

    if (this.sceneReady && this.building3dViewer) {
      this.isolateFloorAndLogRooms();
    }
  }

  private applyLeafletView(): void {
    if (!this.floorMap || !this.floorMapEntity) {
      return;
    }

    if (this.syncLeafletView) {
      this.floorMap.setView(
        [this.syncLeafletView.lat, this.syncLeafletView.lng],
        this.syncLeafletView.zoom,
        { animate: false }
      );
      return;
    }

    const mapEntity = this.floorMapEntity;
    if (mapEntity.building?.buildingid === 'w116660354') {
      this.floorMap.setView([36.0875, -115.176681], 17, { animate: false });
    } else if (Array.isArray(mapEntity.locations) && mapEntity.locations.length >= 2) {
      this.floorMap.fitBounds(mapEntity.locations, { animate: false });
    }
  }

  private initLeafletMap(): void {
    if (this.useSharedLeafletBackground || !this.leafletMapElement?.nativeElement || !this.floorMapEntity?.centerpoint) {
      return;
    }

    const mapEl = this.leafletMapElement.nativeElement;
    if (mapEl.clientWidth < 2 || mapEl.clientHeight < 2) {
      if (this.isFullscreenInstance || this.mapVisible) {
        setTimeout(() => this.initLeafletMap(), 100);
      }
      return;
    }

    if (this.floorMap) {
      this.floorMap.remove();
      this.floorMap = undefined;
    }

    const mapEntity = this.floorMapEntity;
    const mapOptions = {
      ...ConfigurationManagerService.configSettings.floorAttributeVisualizer.leaflet.map.options,
      dragging: false,
      zoomControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
      attributionControl: false,
    };
    const centerLat = mapEntity.centerpoint.latitude;
    const centerLng = mapEntity.centerpoint.longitude;

    this.floorMap = L.map(this.leafletMapElement.nativeElement, mapOptions).setView([centerLat, centerLng], 18);

    L.tileLayer(
      ConfigurationManagerService.configSettings.floorAttributeVisualizer.leaflet.tileLayer.url,
      ConfigurationManagerService.configSettings.floorAttributeVisualizer.leaflet.tileLayer.options
    )
      .setOpacity(FLOOR_LEAFLET_TILE_OPACITY)
      .addTo(this.floorMap);

    this.applyLeafletView();

    this.setupLeafletResizeObserver();
    this.floorMap.whenReady(() => {
      this.applyLeafletView();
      this.floorMap?.invalidateSize();
    });
    setTimeout(() => this.refreshLeafletMap(), 0);
  }

  private refreshLeafletMap(): void {
    if (!this.floorMap || !this.floorMapEntity) {
      return;
    }

    this.floorMap.invalidateSize();
    this.applyLeafletView();
  }

  private setupLeafletResizeObserver(): void {
    if (!this.leafletMapElement?.nativeElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    const leafletEl = this.leafletMapElement.nativeElement;
    if (!this.leafletResizeObserver) {
      this.leafletResizeObserver = new ResizeObserver(() => {
        this.floorMap?.invalidateSize();
      });
    }
    this.leafletResizeObserver.observe(leafletEl);
  }

  private async loadBuildingModel(): Promise<void> {
    if (this.assetBundle) {
      this.rawAssetBundle = this.assetBundle;
      this.updateAssetBundleForFloor();
      return;
    }
    if (!this.assetResolutionKey) {
      return;
    }

    try {
      const baseBundle = await firstValueFrom(
        this.buildingModelAssets.getAssetBundle(this.assetResolutionKey)
      );
      this.rawAssetBundle = baseBundle;
      this.updateAssetBundleForFloor();
    } catch (error) {
      console.error('Blender Floor Viewer: Failed to load building model', error);
    }
  }

  private getFloorGlbUrl(bundle: BuildingModelAssetBundle): string {
    if (!bundle || !this.floorMapEntity) {
      return bundle?.glbUrl || '';
    }
    const floors = (bundle as any).floors;
    if (!floors || !Array.isArray(floors) || floors.length === 0) {
      return bundle.glbUrl;
    }

    const targetPerimeterId = String(this.floorMapEntity.perimeterId || this.floorMapEntity.id || '');
    const targetFloorNumber = this.floorMapEntity.floorNumber;

    const matchedFloor = floors.find((f: any) =>
      (f.id && String(f.id) === targetPerimeterId) ||
      (f.token && String(f.token) === targetPerimeterId) ||
      (f.number !== undefined && f.number === targetFloorNumber)
    );

    return matchedFloor?.url || bundle.glbUrl;
  }

  private updateAssetBundleForFloor(): void {
    if (!this.rawAssetBundle) {
      return;
    }
    const targetGlbUrl = this.getFloorGlbUrl(this.rawAssetBundle);
    const updatedBundle: BuildingModelAssetBundle = {
      ...this.rawAssetBundle,
      glbUrl: targetGlbUrl
    };

    this.assetBundle = updatedBundle;
    this.cdr.markForCheck();

    if (this.building3dViewer) {
      this.building3dViewer.reloadWithAssetBundle(updatedBundle);
    }
  }

  onModelMetadataParsed(metadata: any): void {
    this.parsedMetadata = metadata;
    this.sceneReady = true;

    // if (this.liveUserData && this.building3dViewer) {
    //   this.building3dViewer.refreshLiveUserAssets();
    //   return;
    // }

    if (this.liveUserData && this.building3dViewer) {
      const data = this.liveUserData;
      this.liveUserData = null;
      setTimeout(() => {
        this.liveUserData = data;
      }, 0);
    }
    if (this.selectedRoomId) {
      requestAnimationFrame(() => this.building3dViewer?.isolateRoom(String(this.selectedRoomId)));
      return;
    }

    this.isolateFloorAndLogRooms();
  }

  onLoadingChange(loading: boolean): void {
    this.loadingChange.emit(loading);
  }

  private isolateFloorAndLogRooms(): void {
    if (!this.floorMapEntity) {
      return;
    }

    if (!this.building3dViewer) {
      queueMicrotask(() => this.isolateFloorAndLogRooms());
      return;
    }

    const floorRef = this.floorMapEntity.perimeterId ||
      this.floorMapEntity.arcAngelId ||
      this.floorMapEntity.id ||
      this.floorMapEntity.label;

    if (!floorRef) {
      this.building3dViewer?.completeFloorPresentation();
      return;
    }


    // Log rooms from floor perimeters (this is the actual room data)
    if (this.floorMapEntity.perimeters) {
      const floorRooms = Object.values(this.floorMapEntity.perimeters)
        .filter((p: any) => p.perimeterType === 'ROOM')
        .map((room: any) => ({
          roomId: room.id || room.perimeterId || room.arcAngelId,
          roomName: room.name || room.label || 'Unknown Room',
          roomType: room.perimeterType || 'ROOM',
          locations: room.locations
        }));
    }

    // Also log GLB room meshes if available
    if (this.parsedMetadata?.roomMeshes) {
      const glbRooms = Object.entries(this.parsedMetadata.roomMeshes)
        .filter(([_, room]: [string, any]) => {
          const roomFloorId = room.floorId || room.floorStackId || room.parentId;
          return String(roomFloorId) === String(floorRef);
        })
        .map(([roomId, room]: [string, any]) => ({
          roomId,
          roomName: room.displayName || room.name || roomId,
          roomType: room.roomType || room.propertyType || room.type
        }));
    }

    setTimeout(() => {
      // If a specific room is selected, isolate that room instead of the floor
      if (this.selectedRoomId) {
        this.building3dViewer.restoreFloors();
        this.building3dViewer.restoreRoomCutout();
        this.building3dViewer.isolateRoom(this.selectedRoomId);
        this.building3dViewer.renderAlertAssets();

        return;
      }

      // Only isolate floor if no specific room is selected

      this.building3dViewer.isolateFloorByReference(String(floorRef));
      this.building3dViewer.renderAlertAssets();
    }, 0);
  }

  private isolateSelectedRoomInPopup(): void {
    if (!this.selectedRoomId || !this.building3dViewer || !this.parsedMetadata) {
      return;
    }

    setTimeout(() => {
      this.building3dViewer.restoreFloors();
      this.building3dViewer.restoreRoomCutout();
      this.building3dViewer.isolateRoom(this.selectedRoomId!);
      this.building3dViewer.renderAlertAssets();
    }, 300);
  }

  onRoomClicked(event: { roomId: string; floorId: string; mesh: any; roomName?: string; roomType?: string; metadata?: Record<string, unknown> }): void {

    const currentTime = Date.now();
    const emitDebounceMs = 500; // Debounce rapid clicks on same room

    // Only debounce if clicking the SAME room rapidly
    if (this.lastEmittedRoomId === event.roomId && (currentTime - this.lastEmitTime) < emitDebounceMs) {
      return;
    }

    // Prevent processing multiple room clicks simultaneously
    if (this.isProcessingRoomClick) {
      return;
    }

    if (!this.building3dViewer) {
      this.roomClicked.emit(event);
      this.lastEmittedRoomId = event.roomId;
      this.lastEmitTime = currentTime;
      return;
    }

    this.isProcessingRoomClick = true;

    this.roomClicked.emit(event);
    this.lastEmittedRoomId = event.roomId;
    this.lastEmitTime = currentTime;
    this.isProcessingRoomClick = false;
  }

  onUserClicked(event: { userId: string; roomId: string; mesh: any }): void {
    this.userClicked.emit(event);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetBundle']?.currentValue) {
      this.rawAssetBundle = changes['assetBundle'].currentValue;
      this.updateAssetBundleForFloor();
    }

    if (changes['mapVisible'] && this.mapVisible && !this.useSharedLeafletBackground) {
      this.scheduleLeafletInit();
    }

    if (changes['syncLeafletView'] && this.mapVisible && !this.useSharedLeafletBackground && !this.floorMap) {
      this.scheduleLeafletInit();
    }

    if ((changes['syncLeafletView'] || changes['floorMapEntity']) && this.floorMap && !this.useSharedLeafletBackground) {
      this.applyLeafletView();
      setTimeout(() => this.floorMap?.invalidateSize(), 0);
    }

    if (changes['floorMapEntity'] && this.leafletMapElement && !this.useSharedLeafletBackground && !this.floorMap) {
      this.scheduleLeafletInit();
    }

    if (changes['selectedRoomId'] && this.selectedRoomId) {
      if (changes['liveUserData']) {
      }
      this.isolateSelectedRoomInPopup();
    }

    if (changes['liveUserData']) {
      if (this.building3dViewer) {
        this.building3dViewer.refreshLiveUserAssets();
      }
    }

    if (changes['alertRecords'] && this.building3dViewer) {
      this.building3dViewer.renderAlertAssets();
    }

    if (changes['showBeacons'] && this.building3dViewer) {
      this.building3dViewer.showBeacons = this.showBeacons;
      this.building3dViewer.renderBeaconAssets();
    }

    if (changes['floorMapEntity'] && this.building3dViewer) {
      this.updateAssetBundleForFloor();
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
    if (this.leafletInitTimer) {
      clearTimeout(this.leafletInitTimer);
      this.leafletInitTimer = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.leafletResizeObserver?.disconnect();
    this.leafletResizeObserver = null;
    if (this.floorMap) {
      this.floorMap.remove();
      this.floorMap = undefined;
    }
  }
  public triggerResize(): void {
    if (this.building3dViewer) {
      this.building3dViewer.ensureCrispResize();
    }
  }
}
